import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
} from "@material-ui/core";
import { Button, Grid } from "@material-ui/core";
import { FormInputLabel } from "../common/FormInputLabel";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { hasReportOrderItemsAccess, hasReportOrderSummaryAccess } from "../../helpers/entitlementHelper";
import { useOrderReportHandler } from "../../hooks/rest";
import { AxiosPromise } from "axios";

interface IOrderReportsModal {
  stores: any[];
  open: boolean;
  setOpen: any;
  setShowLoader: any;
}

type ReportType = "summary" | "items";

const OrderReportsModal = ({ stores, open, setOpen, setShowLoader }: IOrderReportsModal) => {
  const { t } = useTranslation();

  const { orderSummary, orderItems } = useOrderReportHandler();

  const [store, setStore] = useState<string>("-");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [reportType, setReportType] = useState<ReportType>();

  useEffect(() => {
    setDefaultReport();
  }, []);

  const handleDownloadReport = () => {
    if (startDate === "" && endDate === "") {
      return;
    }

    if (reportType === "summary") {
      downloadOrderSummary();
      return;
    }
    if (reportType === "items") {
      downloadOrderItems();
      return;
    }
  };

  const downloadOrderSummary = () => {
    setShowLoader(true);
    const [response] = orderSummary(startDate, endDate, store === "-" ? "" : store);
    downloadCsv(response);
  };

  const downloadOrderItems = () => {
    setShowLoader(true);
    const [response] = orderItems(startDate, endDate, store === "-" ? "" : store);
    downloadCsv(response);
  };

  const downloadCsv = (response: AxiosPromise<any>) => {
    response
      .then((data) => {
        const blob = new Blob([data.data], { type: "text/csv" });

        const a = document.createElement("a");
        a.download = `report-orders-${reportType}.csv`;
        a.href = URL.createObjectURL(blob);
        a.addEventListener("click", (e) => {
          setTimeout(() => URL.revokeObjectURL(a.href), 30 * 1000);
        });
        a.click();
      })
      .finally(() => {
        setShowLoader(false);
      });
  };

  const handleClose = () => {
    setOpen(false);
    setDefaults();
  };

  const setDefaults = () => {
    setStore("-");
    setStartDate("");
    setEndDate("");
    setDefaultReport();
  };

  const setDefaultReport = () => {
    if (hasReportOrderSummaryAccess()) {
      setReportType("summary");
      return;
    }
    if (hasReportOrderItemsAccess()) {
      setReportType("items");
      return;
    }
  };

  const renderStoreFilter = () => (
    <Grid item xs={12} sm={6} xl={4} md={4} className="caja-form">
      <FormInputLabel text={t("OrderManagement.ReportFilters.Store")} />
      <TextField
        select
        value={store || "-"}
        variant="outlined"
        size="small"
        fullWidth
        onChange={(e) => setStore(e.target.value)}>
        <MenuItem key="-" value="-">
          Todas
        </MenuItem>
        {stores &&
          stores.map((item: any) => (
            <MenuItem key={item.storeId} value={item.storeId}>
              {item.name}
            </MenuItem>
          ))}
      </TextField>
    </Grid>
  );

  const renderStartDateFilter = () => (
    <Grid item xs={12} sm={6} xl={4} md={4} className="caja-form">
      <FormInputLabel text={t("OrderManagement.ReportFilters.StartDate")} />
      <TextField
        type="date"
        value={startDate || ""}
        variant="outlined"
        size="small"
        fullWidth
        onChange={(e) => setStartDate(e.target.value)}></TextField>
    </Grid>
  );

  const renderEndDateFilter = () => (
    <Grid item xs={12} sm={6} xl={4} md={4} className="caja-form">
      <FormInputLabel text={t("OrderManagement.ReportFilters.EndDate")} />
      <TextField
        type="date"
        value={endDate || ""}
        variant="outlined"
        size="small"
        fullWidth
        onChange={(e) => setEndDate(e.target.value)}></TextField>
    </Grid>
  );

  return (
    <Dialog open={open} onClose={handleClose}>
      <div className="order-report-modal">
        <DialogTitle>
          <Grid container>
            {renderStoreFilter()}
            {renderStartDateFilter()}
            {renderEndDateFilter()}
          </Grid>
          <Button
            onClick={handleClose}
            style={{
              position: "absolute",
              top: 8,
              right: 8,
            }}>
            X
          </Button>
        </DialogTitle>

        <DialogContent style={{ paddingTop: "0" }}>
          <TableContainer component={Paper}>
            <Table aria-label="Bodegas disponibles">
              <TableBody>
                {hasReportOrderSummaryAccess() && (
                  <TableRow key="summary">
                    <TableCell>
                      <Checkbox
                        checked={reportType === "summary"}
                        onChange={(e) => setReportType("summary")}
                        style={{ borderRadius: "50%" }}></Checkbox>
                      {t("OrderManagement.ReportOrderSummary")}
                    </TableCell>
                  </TableRow>
                )}
                {hasReportOrderItemsAccess() && (
                  <TableRow key="items">
                    <TableCell>
                      <Checkbox
                        checked={reportType === "items"}
                        onChange={(e) => setReportType("items")}
                        style={{ borderRadius: "50%" }}></Checkbox>
                      {t("OrderManagement.ReportOrderItems")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleDownloadReport} variant="contained" color="primary">
            Descargar reporte
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  );
};

export default OrderReportsModal;
